/**
 * web/src/screens/Notebook/hooks/capture.ts — the capture / quick-add seam of
 * useNotebookState (card vncVrGk1hc9i, round two): the one capture bar, its
 * pills and collection, the link receipt and its mutations, and the single
 * save path. Pure move out of the former hooks.ts — no behaviour change.
 */
import { useCallback, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { api } from '../../../shell/bridge';
import { createDateIdea } from '../../../lib/dateIdeas';
import { detectCaptureType, urlInCapture } from '../capture-type';
import { ENTRY_PLURAL, type EntryType, type NotebookItem } from '../types';
import type { NotebookDates } from './dates';
import type { NotebookFeed } from './feed';
import type { NotebookShare } from './share';

export function useNotebookCapture({ reload, reloadRef, loadDates, setDateSaved, draftSharedFor, setSharedDraft, setGiftDraft, giftAck }: {
  reload: NotebookFeed['reload'];
  reloadRef: NotebookFeed['reloadRef'];
  loadDates: NotebookDates['loadDates'];
  setDateSaved: NotebookDates['setDateSaved'];
  draftSharedFor: NotebookShare['draftSharedFor'];
  setSharedDraft: NotebookShare['setSharedDraft'];
  setGiftDraft: NotebookShare['setGiftDraft'];
  giftAck: string;
}) {
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);

  /* Desktop uplift phase 2b (card quWhYyrNiIbt item 1, PRD §6.2) — ONE capture
     bar replaces the two composers (typed quick-add + link paste).

     `quickType` is now the bar's TYPE: null means "let the text decide"
     (detectCaptureType — a URL is a link, a price is a gift, words are a
     note), and any explicit choice — the chip, or picking a collection tab —
     pins it. `draft` is the single text field both old composers shared.
     `justSavedId` drives the insertion highlight (C5), and `captureToast` is
     the saved-with-Undo line that follows a typed save home. */
  const [quickType, setQuickType] = useState<EntryType | null>(null);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [collection, setCollection] = useState<'all' | EntryType>('all');
  const [justSavedId, setJustSavedId] = useState<string | null>(null);
  const [captureToast, setCaptureToast] = useState<{ itemId: string | null; text: string } | null>(null);
  const captureToastTimer = useRef<number | undefined>(undefined);

  /* The receipt for the link just saved (card VPlGeOn7LJPI, PRD §6.1): "Filed to
   * Events › Gigs · Shared. Change." Undo is here, not in a dialog, because the
   * one thing a mis-paste needs is the row gone. The card exists before its
   * verdict does, so the receipt starts as "still reading" and refines to the
   * home the routing table sent it to once the classifier answers. */
  const [receipt, setReceipt] = useState<{
    itemId: string;
    kind: string;
    scope: 'shared' | 'just_me';
    filing: boolean;
    undone: boolean;
    /** A mutation is in flight — both buttons are disabled while it runs. */
    busy: boolean;
    /** The last mutation's failure, said out loud (sol review 2026-10-17):
     *  a console line is not a receipt. */
    error: string | null;
    /** The cascade band the poll saw (card xNGOA5rZz9LV phase 3): an 'ask'
     *  row must say it is unsure, and a 'check' row must not read as settled. */
    tier: 'auto' | 'check' | 'ask' | null;
  } | null>(null);

  // Card o79UE71eZgBs: a save used to CLOSE the typed composer, so capturing
  // three memories meant re-opening it three times and the quick entry
  // vanished the moment it did its job. The composer now stays open with the
  // field cleared, and this carries the short inline confirmation naming the
  // composer that answered — it is a type, not a boolean, because the line
  // reads "Saved to your memories" / "…your gifts".
  const [quickSaved, setQuickSaved] = useState<EntryType | null>(null);
  const quickSavedTimer = useRef<number | undefined>(undefined);
  // Gift enrichment poll budget (card OviOTaNsNivQ): the search runs AFTER
  // the save has answered, so the card starts 'pending' and only a refetch
  // can see the detail land. Bounded so a stalled engine cannot turn into an
  // infinite loop of GETs.
  const enrichPasses = useRef(0);
  /* Sol review 2026-10-19 (card xNGOA5rZz9LV phase 3, high): add / quickAdd /
   * toggleDone used to report a failure to the console only — the screen looked
   * exactly like a success, the draft was still sitting there, and the natural
   * reaction was to press Save again and duplicate the row. Each surface now
   * owns its own out-loud error. The drafts are ALREADY preserved on failure
   * (they only clear on a confirmed save), so the message is the missing half. */
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [quickError, setQuickError] = useState<string | null>(null);

  /** The type the bar will save as: an explicit choice (chip, or the tab you
   *  picked) wins, otherwise the text decides (PRD §6.2 C2). */
  const linkTab = collection === 'all' || collection === 'link';
  const detectedType: EntryType = detectCaptureType(draft);
  /* An explicit choice always wins: the tab you are on, or the chip you
     picked. Detection only fills the gap, which is why the chip is editable —
     a pinned Memory stays a Memory even when the words carry a URL (the
     server files that URL through its link pipeline, as before). */
  const captureType: EntryType = quickType ?? detectedType;


  /** One pill click does both jobs: narrow the list to that collection and
   *  point the capture bar at that type. Everything / Link leave the bar on
   *  auto-detect — the bar is shared now, so 'Link' is not a separate form. */
  const pickPill = (key: 'all' | EntryType) => {
    setCollection(key);
    setQuickType(key === 'all' || key === 'link' ? null : key);
    setTypeMenuOpen(false);
    setQuickSaved(null);
    /* Picking a shelf narrows the list and points the composer's chip at that
       type. It does NOT open it (Jonathan 2026-10-30, card bm6DKtgHj3xM): the
       sheet is the person's own act, so the only door is the compact row. */
  };

  /** Escape ends a capture: text gone, type back to auto, caret out of the
   *  bar (PRD §6.2 C4 — "Esc clears"). Nothing is saved. */
  const clearCapture = useCallback(() => {
    setDraft('');
    setQuickType(null);
    setTypeMenuOpen(false);
    setQuickSaved(null);
    setSharedDraft('us'); // next entry defaults back to shared
    setGiftDraft('me'); // …and the next GIFT defaults back to just me
    (document.getElementById('nb-quick') as HTMLTextAreaElement | null)?.blur();
  }, []);

  /* Founder email 2026-10-21 (card ANrPXTBtF2B6) — the quick-entry composer
     can be CLOSED, on request, with one X. Card mG5eA32qztRg (Jonathan
     2026-10-24) flips the default: the bar now starts CLOSED, so the feed
     owns the screen and capture happens when it is wanted — tap the compact
     row, paste anywhere, or pick a shelf. Closing still discards nothing it
     should not: the X and Escape reset the draft through clearCapture, while
     a scroll collapse keeps it (see the effect below). */
  const [captureOpen, setCaptureOpenState] = useState(false);
  const captureOpenRef = useRef(false);
  /** When the bar was last opened — the scroll-collapse rule needs it, and the
      declaration has to sit above the setter that stamps it (mG5eA32qztRg). */
  const captureOpenedAt = useRef(0);
  const setCaptureOpen = useCallback((open: boolean) => {
    if (open) captureOpenedAt.current = Date.now();
    captureOpenRef.current = open;
    setCaptureOpenState(open);
  }, []);
  const openCapture = useCallback(() => {
    setCaptureOpen(true);
    window.setTimeout(() => {
      const el = document.getElementById('nb-quick') as HTMLTextAreaElement | null;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }, 0);
  }, [setCaptureOpen]);
  const closeCaptureNow = useCallback(() => {
    clearCapture();
    setCaptureOpen(false);
  }, [clearCapture, setCaptureOpen]);

  /* Card 9wlZ8NbjACIY — tap-AWAY dismissal: the sheet closes, the draft
     stays. A stray tap on the feed must never throw away words, which is the
     promise the compact bar already made (only the X and Escape clear). */
  const dismissCapture = useCallback(() => {
    setCaptureOpen(false);
  }, [setCaptureOpen]);

  /* Escape is still the CANCEL (PRD §6.2 C4, "Esc clears"), and it belongs to
     the sheet whoever holds focus. This listener is registered at MOUNT on
     purpose: child effects (the overlay primitive's own window listener)
     register first on every commit, so beating it to the node once is what
     makes the ordering stable. Two jobs — an inner surface that owns Escape
     (the (?) help bubble, the type menu) gets the key: the event is marked
     handled so the overlay does not ALSO close, and the inner handler still
     runs; otherwise this is the cancel: clear the draft, close the sheet.
     Guarded on the sheet really being rendered (the overlay slot is
     exclusive, so capture can be "open" while another dialog holds it). */

  /* Card 9wlZ8NbjACIY — the scroll collapse went with the inline bar: the
     sheet pops in OVER the feed without locking it (a wheel over the feed
     scrolls the feed, a wheel over the sheet scrolls the sheet), so there is
     no collapse left to fire. Dismissal is tap-away (draft kept) or X /
     Escape (draft cleared). */

  /** Founder email 2026-10-19: after an entry the bar RESETS ITSELF — field
   *  cleared, focus back in the field — so the next capture starts without a
   *  refresh and without hunting for a close button. */
  const resetQuickField = useCallback((prevBody: string) => {
    setDraft(cur => (cur.trim() === prevBody ? '' : cur));
    window.setTimeout(() => {
      const el = document.getElementById('nb-quick') as HTMLTextAreaElement | null;
      if (el && document.activeElement !== el) el.focus();
    }, 0);
  }, []);

  /** The bar stays open after a save; this is the inline confirmation
   *  and its 4s life. Re-saving inside that window replaces the timer rather
   *  than stacking a second one, so the line cannot vanish early. */
  const flashQuickSaved = useCallback((type: EntryType) => {
    setQuickSaved(type);
    window.clearTimeout(quickSavedTimer.current);
    quickSavedTimer.current = window.setTimeout(() => setQuickSaved(null), 4000);
  }, []);

  /** The saved-with-Undo line (PRD §6.2 C5). It names the home the entry went
   *  to and holds the row's id, so Undo removes exactly that row. */
  const flashCaptureToast = useCallback((itemId: string | null, text: string) => {
    setCaptureToast({ itemId, text });
    window.clearTimeout(captureToastTimer.current);
    captureToastTimer.current = window.setTimeout(() => setCaptureToast(null), 8000);
  }, []);

  const undoCapture = useCallback(async (itemId: string) => {
    try {
      await api(`/api/notebook/${itemId}`, { method: 'PUT', body: { removed: true } });
      setCaptureToast(null);
      setJustSavedId(cur => (cur === itemId ? null : cur));
      await reloadRef.current?.();
    } catch (err) {
      console.error('[Pear] capture undo failed', err);
    }
  }, []);

  // Escape clears the bar from wherever focus sits inside it (C4).
  const captureKeyDown = (e: ReactKeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    /* Escape is one of the two paths back to the compact row (mG5eA32qztRg):
       draft cleared by clearCapture, bar closed here. */
    clearCapture();
    setCaptureOpen(false);
  };

  /* ── The one capture save (card quWhYyrNiIbt item 1, PRD §6.2) ───────────
     The bar replaced two composers, so there is ONE submit path and it
     dispatches on the bar's type:
       link   -> POST /api/notebook {url, description, shared}; the receipt
                 (card VPlGeOn7LJPI) opens on the row the server created
       date   -> POST /api/dates — the couple's date POOL, not a notebook
                 card (the single-pipeline decision, PRD §3)
       gift   -> a URL inside it takes the server's link pipeline with
                 gift:true; words-only saves as a plain gift entry
       others -> POST /api/notebook {entryType, body, shared}
     C6: the words are stored as typed — detection only chooses the type. */
  const captureSave = async (e: FormEvent) => {
    e.preventDefault();
    const raw = draft.trim();
    if (!raw || adding) return;
    const type = captureType;
    setAdding(true);
    setCaptureError(null);
    setQuickError(null);
    try {
      if (type === 'date') {
        await createDateIdea({ title: raw });
        resetQuickField(raw);
        setDateSaved(true);
        window.setTimeout(() => setDateSaved(false), 4000);
        flashQuickSaved('date');
        flashCaptureToast(null, 'Saved to your date ideas — it comes up next time you plan.');
        await loadDates();
        /* Saved: the bar goes back to its compact row (mG5eA32qztRg). The
           saved-with-Undo line is the toast below the sticky block, so the
           confirmation and its Undo survive the collapse. */
        setCaptureOpen(false);
        return;
      }

      const url = urlInCapture(raw);
      const words = url ? raw.replace(url, ' ').replace(/\s+/g, ' ').trim().slice(0, 500) : '';
      /* Card ggTQe2KbB0A3 HIGH-3: read the family's OWN draft, so a gift
         sends 'me' by default instead of inheriting the note that came before
         it. */
      const shared = draftSharedFor(type) === 'us';

      if (type === 'link' || (type === 'gift' && url)) {
        const res = await api<{ item?: { itemId: string; kind?: string; scope?: 'shared' | 'just_me'; entryType?: EntryType } }>('/api/notebook', {
          body: url
            ? { url, shared, ...(type === 'gift' ? { gift: true } : {}), ...(words ? { description: words } : {}) }
            /* Defensive: a link-typed capture with no URL is words, and must
               never be posted as a url (the server would store the sentence). */
            : { body: raw, entryType: 'note', shared },
        });
        const it = res?.item;
        const filedAsLink = (it?.entryType ?? 'link') === 'link';
        if (it?.itemId) {
          if (filedAsLink) {
            /* The receipt (card VPlGeOn7LJPI) already carries Undo, so a
               link save does not also get the toast — one confirmation. */
            setReceipt({ itemId: it.itemId, kind: it.kind ?? 'other', scope: it.scope ?? 'shared', filing: (it.kind ?? 'other') === 'other', undone: false, busy: false, error: null, tier: null });
            void trackFiling(it.itemId);
            /* Card Mpah9C81s6Fn: a card the current filter hides reads as a
               lost save — a link saved from Gift/Note/… follows the card home
               (Everything and Link already show it, so they stay put). */
            if (collection !== 'all' && collection !== 'link') setCollection('link');
          } else {
            flashCaptureToast(it.itemId, type === 'gift' ? 'Saved to your gifts.' : 'Saved to your notebook.');
            setJustSavedId(it.itemId);
            window.setTimeout(() => setJustSavedId(cur => (cur === it.itemId ? null : cur)), 1800);
            setCollection(type === 'gift' ? 'gift' : 'all');
          }
        }
        resetQuickField(raw);
        setSharedDraft('us');
        setGiftDraft('me'); // a gift starts private again for the next save
        /* Saved: back to the compact row (mG5eA32qztRg); the receipt below
           carries the confirmation and its own Undo. */
        setCaptureOpen(false);
        await reload();
        window.setTimeout(() => { void reload(); }, 2500);
        return;
      }

      const res = await api<{ item?: NotebookItem; acknowledgement?: string }>('/api/notebook', {
        body: { entryType: type, body: raw, shared },
      });
      /* The server answers a gift save with the concierge's acknowledgement
         (card OviOTaNsNivQ) — assert the shared line, then let the pending
         poll below watch the detail land on the card. */
      if (type === 'gift') {
        enrichPasses.current = 0;
        if (res?.acknowledgement !== undefined && res.acknowledgement !== giftAck) {
          console.warn('[Pear] gift acknowledgement drifted from the shared line');
        }
      }
      /* Card Mpah9C81s6Fn: a bare URL saved as words is filed as a Link card
         server-side, and a card the current filter hides reads as a lost
         save — so follow it to the collection that holds it. */
      const item = res?.item;
      const reclassified = item?.entryType === 'link' && collection !== 'link';
      if (reclassified) setCollection('link');
      if (item?.itemId) {
        setJustSavedId(item.itemId);
        window.setTimeout(() => setJustSavedId(cur => (cur === item.itemId ? null : cur)), 1800);
        flashCaptureToast(item.itemId, `Saved to your ${ENTRY_PLURAL[type].toLowerCase()}.`);
      }
      resetQuickField(raw);
      if (!reclassified) flashQuickSaved(type);
      setSharedDraft('us');
      setGiftDraft('me'); // …and a gift typed after a note starts private
      /* Saved: back to the compact row (mG5eA32qztRg) — the Undo toast below
         the sticky block stays put, so one tap still takes the row back. */
      setCaptureOpen(false);
      await reload();
    } catch (err) {
      console.error('[Pear] notebook capture failed', err);
      /* Out loud, and the draft stays: a cleared field on a failed save reads
         as success and invites the duplicate press (card xNGOA5rZz9LV).
         The announcement lands in the slot the person was USING: a capture
         carrying a URL failed down the link pipeline, so it speaks where the
         paste happened (nb-link-error); plain words use the typed slot
         (nb-quick-error). One message renders either way. */
      const msg = "That didn't save — your text is still here. Try again.";
      if (urlInCapture(raw)) setCaptureError(msg);
      else setQuickError(msg);
    } finally {
      setAdding(false);
    }
  };

  /* The receipt half of the save flow (card VPlGeOn7LJPI): the paste answers
     before the unfurl and the classifier do, so the receipt is set from the row
     the server just created and then refines itself as the verdict lands. It
     gives up after ~12s and says so rather than spinning forever on a host that
     never answered. */
  const trackFiling = async (itemId: string) => {
    for (let i = 0; i < 6; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await api<{ items: NotebookItem[] }>('/api/notebook');
        const it = (res.items ?? []).find(x => x.itemId === itemId);
        if (it) {
          setReceipt(cur => (cur && cur.itemId === itemId
            ? { ...cur, kind: it.kind, scope: it.scope, tier: it.kindTier ?? cur.tier, filing: it.kind === 'other' && !it.categoryReason && !it.kindTier }
            : cur));
          /* Once the band lands the cascade has ANSWERED — an 'ask' verdict is
             final until a person chooses, so there is nothing left to poll for. */
          if (it.kind !== 'other' || it.categoryReason || it.kindTier) return;
        }
      } catch {
        /* keep the receipt as it stands — the row is already saved */
      }
    }
    setReceipt(cur => (cur && cur.itemId === itemId ? { ...cur, filing: false } : cur));
  };

  /** Every receipt mutation is aimed at ONE row and reports only on that row
   *  (sol review 2026-10-17): a paste made while a request is in flight installs
   *  a NEW receipt, and an unguarded updater would stamp "Removed" or the wrong
   *  audience onto it. The target id is captured up front, the buttons are
   *  disabled while the mutation runs, and a failure is shown in the receipt
   *  rather than only logged. */
  const mutateReceipt = async (target: string, run: () => Promise<void>, apply: (cur: NonNullable<typeof receipt>) => NonNullable<typeof receipt>) => {
    setReceipt(cur => (cur && cur.itemId === target ? { ...cur, busy: true, error: null } : cur));
    try {
      await run();
      setReceipt(cur => (cur && cur.itemId === target ? { ...apply(cur), busy: false, error: null } : cur));
      await reload();
    } catch (err) {
      console.error('[Pear] notebook receipt action failed', err);
      setReceipt(cur => (cur && cur.itemId === target
        ? { ...cur, busy: false, error: 'That didn\'t save — the link is unchanged. Try again.' }
        : cur));
    }
  };

  /** Undo the paste (PRD §6.1): remove the row outright — only its author can. */
  const undoReceipt = async () => {
    if (!receipt || receipt.undone || receipt.busy) return;
    const target = receipt.itemId;
    await mutateReceipt(
      target,
      async () => { await api(`/api/notebook/${target}`, { method: 'PUT', body: { removed: true } }); },
      cur => ({ ...cur, undone: true, filing: false }),
    );
  };

  /** Change the one thing the receipt can change safely: who sees it. */
  const flipReceiptScope = async () => {
    if (!receipt || receipt.undone || receipt.busy) return;
    const target = receipt.itemId;
    const next = receipt.scope !== 'shared';
    await mutateReceipt(
      target,
      async () => { await api(`/api/notebook/${target}`, { method: 'PUT', body: { shared: next } }); },
      cur => ({ ...cur, scope: next ? 'shared' : 'just_me' }),
    );
  };

  return {
    draft, setDraft, adding, setAdding,
    quickType, setQuickType, typeMenuOpen, setTypeMenuOpen,
    collection, setCollection, justSavedId, setJustSavedId,
    captureToast, setCaptureToast, captureToastTimer,
    receipt, setReceipt,
    quickSaved, setQuickSaved, quickSavedTimer,
    enrichPasses, captureError, setCaptureError, quickError, setQuickError,
    linkTab, detectedType, captureType, pickPill, clearCapture,
    captureOpen, setCaptureOpenState, captureOpenRef, captureOpenedAt,
    setCaptureOpen, openCapture, closeCaptureNow, dismissCapture,
    resetQuickField, flashQuickSaved, flashCaptureToast, undoCapture, captureKeyDown,
    captureSave, trackFiling, mutateReceipt, undoReceipt, flipReceiptScope,
  };
}

export type NotebookCapture = ReturnType<typeof useNotebookCapture>;
